import { SearchOutlined } from '@ant-design/icons';
import { history, useAccess } from '@umijs/max';
import { useTenant } from '@zhiyue/core';
import { Button, Empty, Input, Modal, message, Space, Spin } from 'antd';
import React from 'react';
import { businessManagementApi } from '@/services/bff/business-management.api';
import { contentManagementApi } from '@/services/bff/content-management.api';
import { siteManagementApi } from '@/services/bff/site-management.api';

type SearchResult = { key: string; label: string; meta: string; path: string };

export const CmsHeaderTools: React.FC = () => {
  const { currentTenantUuid } = useTenant();
  const access = useAccess();
  const [open, setOpen] = React.useState(false);
  const [search, setSearch] = React.useState('');
  const [searching, setSearching] = React.useState(false);
  const [results, setResults] = React.useState<SearchResult[] | null>(null);
  const requestId = React.useRef(0);
  React.useEffect(() => {
    requestId.current += 1;
    setResults(null);
    setSearch('');
    setSearching(false);
    setOpen(false);
  }, [currentTenantUuid]);
  const close = () => {
    requestId.current += 1;
    setSearching(false);
    setOpen(false);
  };
  const runSearch = async () => {
    const keyword = search.trim();
    if (!keyword || searching) return;
    const id = ++requestId.current;
    setSearching(true);
    setResults(null);
    try {
      const [contents, sites, products] = await Promise.all([
        access.cmsContent
          ? contentManagementApi.list({ page: 1, limit: 5, keyword })
          : Promise.resolve({ data: [] }),
        access.cmsSite
          ? siteManagementApi.list({ page: 1, limit: 3, keyword })
          : Promise.resolve({ data: [] }),
        access.cmsProduct
          ? businessManagementApi.products.list({ page: 1, limit: 5, keyword })
          : Promise.resolve({ data: [] }),
      ]);
      if (id !== requestId.current) return;
      setResults([
        ...contents.data.map((item) => ({
          key: `content:${item.uuid}`,
          label: item.title,
          meta: `内容 · ${item.ownerName}`,
          path: `/content/master/${item.uuid}/edit`,
        })),
        ...sites.data.map((item) => ({
          key: `site:${item.uuid}`,
          label: item.name,
          meta: `站点 · ${item.domain}`,
          path: `/site?keyword=${encodeURIComponent(item.name)}`,
        })),
        ...products.data.map((item) => ({
          key: `product:${item.uuid}`,
          label: item.name,
          meta: '产品 / 服务',
          path: `/product/${item.uuid}/overview`,
        })),
      ]);
    } catch (reason) {
      if (id === requestId.current)
        message.error(
          `搜索失败：${reason instanceof Error ? reason.message : '请稍后重试'}`,
        );
    } finally {
      if (id === requestId.current) setSearching(false);
    }
  };
  return (
    <>
      <Button
        type="text"
        aria-label="全局搜索"
        title="全局搜索"
        icon={<SearchOutlined />}
        onClick={() => setOpen(true)}
      />
      <Modal
        title="全局搜索"
        open={open}
        onCancel={close}
        footer={null}
        width={620}
        focusable={{ focusTriggerAfterClose: true }}
      >
        <Space.Compact block>
          <Input
            autoFocus
            allowClear
            aria-label="搜索内容、站点、产品"
            placeholder="搜索内容、站点、产品…"
            value={search}
            onChange={(event) => setSearch(event.target.value)}
            onPressEnter={() => void runSearch()}
          />
          <Button
            type="primary"
            loading={searching}
            onClick={() => void runSearch()}
          >
            搜索
          </Button>
        </Space.Compact>
        <Spin spinning={searching}>
          <div className="cms-global-search-results" aria-live="polite">
            {results?.map((item) => (
              <button
                key={item.key}
                type="button"
                className="cms-global-search-result"
                onClick={() => {
                  close();
                  history.push(item.path);
                }}
              >
                <span>{item.label}</span>
                <small>{item.meta}</small>
              </button>
            ))}
            {results?.length === 0 && (
              <Empty description="没有找到匹配的内容、站点或产品" />
            )}
          </div>
        </Spin>
      </Modal>
    </>
  );
};
